import { useEffect, useRef, useState } from 'react';

// Single source for the minimum duration (ms) a discount loading indicator
// stays visible once it becomes active, so a fast validation still surfaces a
// perceptible loading state instead of flashing by. Shared by DiscountLoading,
// DiscountsDrawer, and the Remittance loading-hidden timer.
export const DISCOUNT_LOADING_MIN_VISIBLE_MS = 2000;

// Holds a flag visible for a minimum duration once it becomes active. Used so a
// fast discount validation still shows a perceptible loading state (e.g. the
// #DFE0FF4D background) instead of flashing by and reading as "no loading".
export const useMinVisibleFlag = (
  isActive,
  minVisibleMs = DISCOUNT_LOADING_MIN_VISIBLE_MS,
) => {
  const [isVisible, setVisible] = useState(Boolean(isActive));
  const visibleStartedAtRef = useRef(0);
  const hideTimeoutRef = useRef(null);

  useEffect(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    if (isActive) {
      visibleStartedAtRef.current = Date.now();
      setVisible(true);
      return undefined;
    }

    const elapsedMs = Date.now() - visibleStartedAtRef.current;
    const remainingMs = Math.max(minVisibleMs - elapsedMs, 0);

    if (!visibleStartedAtRef.current || remainingMs === 0) {
      setVisible(false);
      return undefined;
    }

    hideTimeoutRef.current = setTimeout(() => {
      setVisible(false);
      hideTimeoutRef.current = null;
    }, remainingMs);

    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [isActive, minVisibleMs]);

  return isVisible;
};
